import { useController, useFormContext } from 'react-hook-form';
import Clock from '@/components/Custom/CustomClock';
import {
  convertDisplayTimeToElapsedMs,
  convertElapsedMsToDisplayTime,
} from '@/components/Event/EventClock/EventClock.utils';
import Box from '@/components/UI/Box';
import Select from '@/components/UI/Select';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import type { EventScorekeepingPanelFormValues } from '../../EventScorekeeping.types';

export const EventScorekeepingPanelPreviewTimeClockFragment = graphql(
  /* GraphQL */ `
    fragment EventScorekeepingPanelPreviewTimeClock on event_clock {
      elapsed_segment_ms
      event_segment {
        id
        title
        duration
        duration_offset
      }
    }
  `,
);

export const EventScorekeepingPanelPreviewTimeFragment = graphql(/* GraphQL */ `
  fragment EventScorekeepingPanelPreviewTime on event {
    id
    event_clock {
      ...EventScorekeepingPanelPreviewTimeClock
    }
    event_segment_group {
      id
      direction
      event_segments(order_by: { order: asc_nulls_last }) {
        id
        title
        duration
        duration_offset
      }
    }
  }
`, [EventScorekeepingPanelPreviewTimeClockFragment]);

export default function EventScorekeepingPanelPreviewTime({
  fragment,
}: {
  fragment:
    | FragmentOf<typeof EventScorekeepingPanelPreviewTimeFragment>
    | null
    | undefined;
}) {
  const event = readFragment(
    EventScorekeepingPanelPreviewTimeFragment,
    fragment,
  );

  const { control, setValue } =
    useFormContext<EventScorekeepingPanelFormValues>();

  const selectedSegmentMs = useController({
    control: control,
    name: 'selectedTime.elapsed_segment_ms',
  });

  const elapsedSegmentMs = selectedSegmentMs.field.value;

  const eventSegment = useController({
    control: control,
    name: 'selectedTime.event_segment',
  });

  const segment = eventSegment.field.value;
  const actualDirection = (event?.event_segment_group?.direction ?? 'desc') as
    | 'asc'
    | 'desc';

  const duration = segment?.duration ?? 0;
  const durationOffset = segment?.duration_offset ?? null;

  const displayTime = convertElapsedMsToDisplayTime(
    elapsedSegmentMs ?? 0,
    actualDirection,
    duration,
    durationOffset,
  );

  const segments = event?.event_segment_group?.event_segments || [];

  if (!segment && segments.length === 0) {
    return null;
  }

  return (
    <Box direction="horizontal" p="md" space="sm" align="center">
      <Select
        placeholder="Select..."
        testID="ScorekeepingAssignmentSegmentSelect"
        key={segment?.id || 'select-segment'}
        value={segment?.id || null}
        onValueChange={(value) => {
          const selectedSegment = segments.find((seg) => seg.id === value);

          if (selectedSegment) {
            setValue('selectedTime.event_segment', selectedSegment, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }
        }}
        disabled={segments.length === 0}
        width={128}
        options={segments.map((segment) => ({
          label: segment.title,
          value: segment.id,
        }))}
      />
      {segment ? (
        <Clock
          isBottomSheet
          maxSeconds={segment.duration / 1000}
          testIDPrefix="ScorekeepingAssignmentClock"
          onChange={(minutes, seconds) => {
            const duration = segment.duration;
            const durationOffset = segment.duration_offset ?? null;

            const newElapsedSegmentMs = convertDisplayTimeToElapsedMs(
              { minutes, seconds },
              actualDirection,
              duration,
              durationOffset,
            );

            setValue('selectedTime.elapsed_segment_ms', newElapsedSegmentMs, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }}
          displayMinutes={displayTime.minutes}
          displaySeconds={displayTime.seconds}
        />
      ) : null}
    </Box>
  );
}
